import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ScreenService } from '../../../../services/screen-service.service';
import { Subscribable } from '../../../../models/base/subscribable';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-paginator',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class PaginatorComponent extends Subscribable implements OnInit, OnChanges {
  @Input() selectedIndex: number = 0;
  @Output() selectedIndexChange = new EventEmitter<number>();
  @Input() totalPages: number = 0;

  private screenService = inject(ScreenService);
  private cdr = inject(ChangeDetectorRef);

  private totalToDisplay = 4;
  private visibleStartIndex = 0;

  public visiblePages: number[] = [];

  private setVisiblePages(): void {
    const end = Math.min(this.visibleStartIndex + this.totalToDisplay, this.totalPages);
    this.visiblePages = Array.from({ length: end - this.visibleStartIndex }, (_, i) => this.visibleStartIndex + i);
  }

  public pageClicked(index: number): void {
    this.selectedIndex = index;

    if (index < this.visibleStartIndex) {
      this.visibleStartIndex = index;
    } else if (index >= this.visibleStartIndex + this.totalToDisplay) {
      this.visibleStartIndex = index - this.totalToDisplay + 1;
    }

    this.setVisiblePages();
    this.selectedIndexChange.emit(this.selectedIndex);
  }

  public nextClicked(): void {
    if (this.selectedIndex < this.totalPages - 1) {
      this.pageClicked(this.selectedIndex + 1);
    }
  }

  public previousClicked(): void {
    if (this.selectedIndex > 0) {
      this.pageClicked(this.selectedIndex - 1);
    }
  }

  public skipToBeginning(): void {
    this.selectedIndex = 0;
    this.visibleStartIndex = 0;
    this.setVisiblePages();
    this.selectedIndexChange.emit(this.selectedIndex);
  }

  public skipToEnd(): void {
    this.selectedIndex = this.totalPages - 1;
    this.visibleStartIndex = Math.max(this.totalPages - this.totalToDisplay, 0);
    this.setVisiblePages();
    this.selectedIndexChange.emit(this.selectedIndex);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.totalPages) {
      this.setVisiblePages();
    }
  }

  public ngOnInit(): void {
    this.screenService.isMobile$.subscribeWhileAlive({
      owner: this,
      next: isMobile => {
        this.totalToDisplay = isMobile ? 2 : 4;

        if (this.selectedIndex < this.visibleStartIndex) {
          this.visibleStartIndex = this.selectedIndex;
        } else if (this.selectedIndex >= this.visibleStartIndex + this.totalToDisplay) {
          this.visibleStartIndex = this.selectedIndex - this.totalToDisplay + 1;
        }

        const maxStartIndex = Math.max(this.totalPages - this.totalToDisplay, 0);
        const clampedStartIndex = Math.min(this.visibleStartIndex, maxStartIndex);
        this.visibleStartIndex = Math.max(0, clampedStartIndex);

        this.setVisiblePages();
        this.cdr.detectChanges();
      }
    });
  }
}
